import {
  BodyMapReport,
  IncidentReport,
  IncidentSeverityValues,
} from '@lmc/ts-common'
import { Text } from '@lmc/ui-kit/src/general'
import { incidentReportsConstants } from 'locale/incidentReports'
import { TFunction } from 'react-i18next'
import { filterItems } from '.'
import { getIncidentReportSeverityLabel } from './incidentSeverityUtils'
import { Link } from 'react-router-dom'

export const getIncidentDetailsItems = (
  incidentReport: IncidentReport,
  severities: IncidentSeverityValues | undefined,
  nominative: string,
  t: TFunction<string, undefined>
) =>
  filterItems([
    {
      key: 'severity',
      label: <Text>{t(incidentReportsConstants.SEVERITY)}</Text>,
      children: incidentReport.severity && (
        <Text>
          {getIncidentReportSeverityLabel(
            incidentReport.severity,
            severities,
            t
          )}
        </Text>
      ),
    },
    {
      key: 'serviceUsersInvolved',
      label: (
        <Text>
          {t(incidentReportsConstants.OTHER_SERVICE_USERS_INVOLVED, {
            variable: nominative,
          })}
        </Text>
      ),
      children:
        incidentReport?.case?.serviceUsersInvolved.length &&
        incidentReport?.case?.serviceUsersInvolved.length > 0 ? (
          <Text>
            {incidentReport.case.serviceUsersInvolved
              .map((serviceUser) => serviceUser.name)
              .join(', ')}
          </Text>
        ) : undefined,
    },
    {
      key: 'otherPeopleInvolved',
      label: <Text>{t(incidentReportsConstants.OTHER_PEOPLE_INVOLVED)}</Text>,
      children: incidentReport.case?.otherPeopleInvolved && (
        <Text>{incidentReport.case?.otherPeopleInvolved}</Text>
      ),
    },
    {
      key: 'otherWitnesses',
      label: <Text>{t(incidentReportsConstants.OTHER_WITNESS)}</Text>,
      children: incidentReport.case?.otherWitnesses && (
        <Text>{incidentReport.case?.otherWitnesses}</Text>
      ),
    },
    {
      key: 'carersInvolved',
      label: <Text>{t(incidentReportsConstants.CARERES_INVOLVED)}</Text>,
      children:
        incidentReport?.case?.carersInvolved?.length &&
        incidentReport?.case?.carersInvolved.length > 0 ? (
          <Text>
            {incidentReport.case.carersInvolved
              .map((serviceUser) => serviceUser.name)
              .join(', ')}
          </Text>
        ) : undefined,
    },
  ])

export const getRestrictivePracticesItems = (
  incidentReport: IncidentReport,
  t: TFunction<string, undefined>
) =>
  filterItems([
    {
      key: 'physicalIntervention',
      label: (
        <Text>{t(incidentReportsConstants.PHYSICAL_INTERVENTION_APPLIED)}</Text>
      ),
      children: incidentReport.case?.physicalIntervention?.details && (
        <Text>{incidentReport.case?.physicalIntervention?.details}</Text>
      ),
    },
    {
      key: 'restraints',
      label: <Text>{t(incidentReportsConstants.RESTRAINTS_APPLIED)}</Text>,
      children: incidentReport.case?.restraints?.details && (
        <Text>{incidentReport.case?.restraints?.details}</Text>
      ),
    },
    {
      key: 'chemicalRestraints',
      label: (
        <Text>{t(incidentReportsConstants.CHEMICAL_RESTRAINTS_APPLIED)}</Text>
      ),
      children: incidentReport.case?.chemicalRestraints?.details && (
        <Text>{incidentReport.case?.chemicalRestraints?.details}</Text>
      ),
    },
  ])

export const getInjuriesItems = (
  incidentReport: IncidentReport,
  nominative: string,
  bodyMaps: BodyMapReport[],
  t: TFunction<string, undefined>
) => {
  const residentBodyMap = bodyMaps.find(
    (bodyMap) =>
      bodyMap.id ===
      incidentReport.case?.residentInjuries.residentInjuriesBodyMap
  )

  return filterItems([
    {
      key: 'residentInjuries',
      label: (
        <Text>
          {t(incidentReportsConstants.INJURIES_OF_RESIDENT, {
            variable: nominative,
          })}
        </Text>
      ),
      children: incidentReport.case?.residentInjuries?.details ? (
        <Text>{incidentReport.case?.residentInjuries?.details}</Text>
      ) : undefined,
    },
    {
      key: 'residentInjuries.residentInjuriesBodyMap',
      label: <Text>{t(incidentReportsConstants.RELATED_BODY)}</Text>,
      children: incidentReport.case?.residentInjuries
        .residentInjuriesBodyMap && (
        <Link
          to={`/residents/${residentBodyMap?.resident}/charts/body_maps/${residentBodyMap?.id}`}
        >
          <Text>{residentBodyMap?.title}</Text>
        </Link>
      ),
    },
    {
      key: 'carersInjuries',
      label: <Text>{t(incidentReportsConstants.INJURIES_OF_CARERS)}</Text>,
      children: incidentReport.case?.carersInjuries?.details && (
        <Text>{incidentReport.case?.carersInjuries?.details}</Text>
      ),
    },
    {
      key: 'otherPartiesInjuries',
      label: (
        <Text>{t(incidentReportsConstants.INJURIES_OF_OTHER_PARTIES)}</Text>
      ),
      children: incidentReport.case?.otherPartiesInjuries?.details && (
        <Text>{incidentReport.case?.otherPartiesInjuries?.details}</Text>
      ),
    },
  ])
}

export const getDamagesItems = (
  incidentReport: IncidentReport,
  t: TFunction<string, undefined>
) =>
  filterItems([
    {
      key: 'damages',
      label: <Text>{t(incidentReportsConstants.DETAILS)}</Text>,
      children: incidentReport.case?.damages?.details && (
        <Text>{incidentReport.case?.damages?.details}</Text>
      ),
    },
  ])

export const getMedicationsItems = (
  incidentReport: IncidentReport,
  t: TFunction<string, undefined>
) =>
  filterItems([
    {
      key: 'prnMedication',
      label: <Text>{t(incidentReportsConstants.DETAILS)}</Text>,
      children: incidentReport.case?.prnMedication?.details && (
        <Text>{incidentReport.case?.prnMedication?.details}</Text>
      ),
    },
  ])

export const getOpinionItems = (
  incidentReport: IncidentReport,
  t: TFunction<string, undefined>
) =>
  filterItems([
    {
      key: 'serviceUserOpinion',
      label: <Text>{t(incidentReportsConstants.DETAILS)}</Text>,
      children: incidentReport.case?.serviceUserOpinion && (
        <Text>{incidentReport.case?.serviceUserOpinion}</Text>
      ),
    },
  ])

export const getAdditionalItems = (
  incidentReport: IncidentReport,
  t: TFunction<string, undefined>
) =>
  filterItems([
    {
      key: 'incidentOtherInformation',
      label: <Text>{t(incidentReportsConstants.DETAILS)}</Text>,
      children: incidentReport.case?.incidentOtherInformation && (
        <Text>{incidentReport.case?.incidentOtherInformation}</Text>
      ),
    },
  ])

export const getDebriefingItems = (
  incidentReport: IncidentReport,
  t: TFunction<string, undefined>
) => {
  return filterItems([
    {
      key: 'debriefingNotes',
      label: <Text>{t(incidentReportsConstants.NOTES)}</Text>,
      children:
        incidentReport.case?.debriefingNotes?.length &&
        incidentReport.case?.debriefingNotes?.length > 0 ? (
          <Text>{incidentReport.case?.debriefingNotes}</Text>
        ) : undefined,
    },
  ])
}

export const getLearningAndOutcomesItems = (
  incidentReport: IncidentReport,
  t: TFunction<string, undefined>
) => {
  return filterItems([
    {
      key: 'rootCause',
      label: <Text>{t(incidentReportsConstants.ROOT_CAUSE)}</Text>,
      children: incidentReport.case?.rootCause ? (
        <Text>{incidentReport.case?.rootCause}</Text>
      ) : undefined,
    },
    {
      key: 'preventativeActions',
      label: <Text>{t(incidentReportsConstants.ACTIONS_TAKEN)}</Text>,
      children: incidentReport.case?.preventativeActions ? (
        <Text>{incidentReport.case?.preventativeActions}</Text>
      ) : undefined,
    },
    {
      key: 'otherNotes',
      label: <Text>{t(incidentReportsConstants.OTHER_NOTES)}</Text>,
      children: incidentReport.case?.otherNotes ? (
        <Text>{incidentReport.case?.otherNotes}</Text>
      ) : undefined,
    },
  ])
}
